/**
 * 保存ボタン付きの入力のまとまり（docs/06 4-3）。
 *
 * **黙って保存しないための部品です**（docs/06 原則1）。
 * 入力欄を囲み、下に**かならず「保存（＝その画面の言葉）」を置きます**。
 * 自動で保存する画面を作る場合も、保存できたことは `ResultBanner` で必ず画面に出してください。
 *
 * 「やめる」は、**やめたときに戻る先がある画面では必ず置きます**（`onCancel` を渡す）。
 * 戻る先が無い画面（ログインのように、その画面しか無い場面）では置きません。
 * 押しても何も起きないボタンを並べる方が、慣れていない方には分かりにくいためです。
 *
 * ボタンの並びは「保存」が左・「やめる」が右です。確認の窓（`ConfirmDialog`）は逆に
 * 「やめる」が左ですが、これは意図した違いです（docs/06 4-3）。日々くり返す保存は
 * 指の近くに置き、**取り返しのつかない操作だけは指から遠ざける**、という考えです。
 */
import type { FormEvent, ReactNode } from 'react';
import { Button } from './Button.js';

export interface FormCardProps {
  /** まとまりの見出し */
  title?: ReactNode;
  /** 見出しの下の説明 */
  description?: ReactNode;
  /** 保存ボタンの言葉（「保存」「ログイン」「答えを登録」など。動詞で書く） */
  submitLabel: string;
  /** 保存の最中に出す言葉 */
  submitBusyLabel?: string;
  /** 保存の最中か */
  busy?: boolean;
  /** 押したときに走る処理。画面の作り直しは呼ぶ側で行う */
  onSubmit: () => void;
  /** 「やめる」を押したときの処理。戻る先が無い画面では省く（省くと「やめる」は出ない） */
  onCancel?: () => void;
  cancelLabel?: string;
  /** 入力欄の下・ボタンの上に出す注記（結果のお知らせなど） */
  footer?: ReactNode;
  /**
   * 囲みの枠を付けない（囲み〔Card〕の中に置くとき）。
   * 枠の中にさらに枠があると、どこまでが1つのまとまりか分かりにくくなるため（総点検 M10・M15）
   */
  bare?: boolean;
  /**
   * 保存ボタンの見た目。ふだんは主役（赤）。同じ画面に主役がほかにあるときは控え（secondary）にして、
   * 赤いボタンを並べない（#329-9）
   */
  submitVariant?: 'primary' | 'secondary';
  children: ReactNode;
}

export function FormCard({
  title,
  description,
  submitLabel,
  submitBusyLabel,
  busy = false,
  onSubmit,
  onCancel,
  cancelLabel = 'やめる',
  footer,
  bare = false,
  submitVariant = 'primary',
  children,
}: FormCardProps): ReactNode {
  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (busy) return;
    onSubmit();
  }

  return (
    <form className={`ui-form-card${bare ? ' is-bare' : ''}`} onSubmit={handleSubmit}>
      {title !== undefined ? <h2 className="ui-card-title">{title}</h2> : null}
      {description !== undefined ? <p className="ui-card-desc">{description}</p> : null}
      <div className="ui-form-fields">{children}</div>
      {footer !== undefined ? <div className="ui-form-footer">{footer}</div> : null}
      <div className="ui-form-actions">
        <Button
          type="submit"
          variant={submitVariant}
          busy={busy}
          busyLabel={submitBusyLabel ?? `${submitLabel}しています…`}
        >
          {submitLabel}
        </Button>
        {onCancel !== undefined ? (
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
